import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { TournamentDto } from '../../app/models/Tournament';

const SPORT_EMOJI: Record<string, string> = {
  football: '⚽',
  basketball: '🏀',
  tennis: '🎾',
  volleyball: '🏐',
  handball: '🤾',
  futsal: '⚽',
  baseball: '⚾',
  rugby: '🏉',
  hockey: '🏒',
  tabletennis: '🏓',
  badminton: '🏸',
  golf: '⛳',
  swimming: '🏊',
  athletics: '🏃',
  cycling: '🚴',
  boxing: '🥊',
  mma: '🥋',
  esports: '🎮',
};

function getSportEmoji(sportName: string): string {
  const key = sportName.toLowerCase().replace(/[\s_-]/g, '');
  return SPORT_EMOJI[key] ?? '🏆';
}

function formatDateShort(iso: string): string {
  return format(new Date(iso), 'dd MMM yyyy');
}

function formatDateLong(iso: string): string {
  return format(new Date(iso), 'dd MMM yyyy, HH:mm');
}

function getFormatLabel(fmt: string, t: (k: string, opts?: any) => string): string {
  return t(`tournament.format.${fmt.toLowerCase()}`);
}

function getStatusColor(status: string): { bg: string; border: string; text: string } {
  switch (status) {
    case 'RegistrationOpen': return { bg: 'rgba(34,197,94,0.15)', border: 'rgba(34,197,94,0.4)', text: '#22c55e' };
    case 'InProgress':       return { bg: 'rgba(6,182,212,0.15)',  border: 'rgba(6,182,212,0.4)',  text: '#06b6d4' };
    case 'Completed':        return { bg: 'rgba(168,85,247,0.15)', border: 'rgba(168,85,247,0.4)', text: '#a855f7' };
    case 'Cancelled':        return { bg: 'rgba(239,68,68,0.15)',  border: 'rgba(239,68,68,0.4)',  text: '#ef4444' };
    default:                 return { bg: 'rgba(245,158,11,0.15)', border: 'rgba(245,158,11,0.4)', text: '#f59e0b' };
  }
}

// Helper funkcija za sigurno pozivanje t funkcije
function safeT(t: (k: string, opts?: any) => string | any, key: string, opts?: any): string {
  const result = t(key, opts);
  if (typeof result === 'string') {
    return result;
  }
  if (result && typeof result === 'object' && 'toString' in result) {
    return result.toString();
  }
  return String(result || '');
}

function buildPosterHtml(t_: TournamentDto, t: (k: string, opts?: any) => string | any): string {
  // Dohvati base URL iz environment varijable
  const getBaseUrl = () => {
    if (process.env.NODE_ENV === 'production') {
      return process.env.REACT_APP_BASE_URL || 'https://www.sportizan.com';
    }
    if (typeof window !== 'undefined') {
      return window.location.origin;
    }
    return 'http://localhost:3000';
  };

  const baseUrl = getBaseUrl();
  const tournamentUrl = `${baseUrl}/tournaments/${t_.id}`;
  
  const sportEmoji = getSportEmoji(t_.sportName);
  const formatLabel = getFormatLabel(t_.format, t);
  const statusColor = getStatusColor(t_.status);
  const statusLabel = safeT(t, `tournament.status.${t_.status.toLowerCase()}`);

  const dateRange = t_.endsAt
    ? `${formatDateShort(t_.startsAt)} – ${formatDateShort(t_.endsAt)}`
    : formatDateShort(t_.startsAt);

  const entryFeeDisplay = !t_.entryFee || t_.entryFee === 0
    ? safeT(t, 'tournament.poster.free')
    : `€${t_.entryFee}`;

  const entryFeeLabel = !t_.entryFee || t_.entryFee === 0
    ? safeT(t, 'tournament.poster.freeSubtitle')
    : safeT(t, 'tournament.poster.entryFeeSubtitle');

  const registrationModeLabel = t_.registrationMode === 'AutoAccept'
    ? safeT(t, 'tournament.poster.autoAccept')
    : safeT(t, 'tournament.poster.manualReview');

  const registrationModeSubtitle = t_.registrationMode === 'AutoAccept'
    ? safeT(t, 'tournament.poster.autoAcceptSub')
    : safeT(t, 'tournament.poster.manualReviewSub');

  const refereesDisplay = t_.hasReferees
    ? safeT(t, 'tournament.poster.refereesProvided')
    : safeT(t, 'tournament.poster.selfRefereed');

  const refereesSubtitle = t_.hasReferees && t_.minRefereesPerMatch
    ? safeT(t, 'tournament.poster.refereesPerMatch', { count: t_.minRefereesPerMatch })
    : '';

  const rosterFreezeLabel = (() => {
    switch (t_.rosterFreezePolicy) {
      case 'Strict':   return safeT(t, 'tournament.poster.rosterFreezeStrict');
      case 'Open':     return safeT(t, 'tournament.poster.rosterFreezeOpen');
      case 'Flexible': return safeT(t, 'tournament.poster.rosterFreezeFlexible', { days: t_.rosterFreezeDaysBeforeStart ?? 0 });
      default:         return t_.rosterFreezePolicy;
    }
  })();

  const rulesLines: string[] = [];
  rulesLines.push(safeT(t, 'tournament.poster.pointsRule', {
    win: t_.pointsForWin,
    draw: t_.pointsForDraw,
    loss: t_.pointsForLoss,
  }));
  rulesLines.push(safeT(t, 'tournament.poster.disputeRule', { hours: t_.disputeTimeoutHours }));
  if (t_.hasThirdPlaceMatch) rulesLines.push(safeT(t, 'tournament.poster.thirdPlaceMatch'));
  if (t_.numberOfGroups) {
    rulesLines.push(safeT(t, 'tournament.poster.groupsRule', {
      groups: t_.numberOfGroups,
      advancing: t_.teamsAdvancingPerGroup ?? 0,
    }));
  }

  const locationDisplay = t_.isOnline
    ? safeT(t, 'tournament.poster.onlineEvent')
    : safeT(t, 'tournament.poster.physicalLocation');

  const ctaTitle = (() => {
    switch (t_.status) {
      case 'RegistrationOpen': return safeT(t, 'tournament.poster.ctaTitleRegOpen');
      case 'InProgress':       return safeT(t, 'tournament.poster.ctaTitleInProgress');
      case 'Completed':        return safeT(t, 'tournament.poster.ctaTitleCompleted');
      default:                 return safeT(t, 'tournament.poster.ctaTitleDefault');
    }
  })();

  const ctaSubtitle = t_.status === 'RegistrationOpen'
    ? safeT(t, 'tournament.poster.ctaSubtitleRegOpen', { fee: entryFeeDisplay })
    : safeT(t, 'tournament.poster.ctaSubtitleDefault');

  const escapeHtml = (str: string) => {
    return str
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  };

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>${escapeHtml(t_.name)} – ${safeT(t, 'tournament.poster.documentTitle')}</title>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js"></script>
  <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=Inter:wght@300;400;500;600;700;800&display=swap" rel="stylesheet">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      background: #0a0f1a;
      min-height: 100vh;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 20px;
      font-family: 'Inter', sans-serif;
    }
    .poster {
      width: 794px;
      height: 1200px;
      background: linear-gradient(160deg, #0a1628 0%, #0d2b4e 40%, #0a3a6a 70%, #0a4a7a 100%);
      position: relative;
      overflow: hidden;
      padding: 40px 50px;
      box-shadow: 0 25px 80px rgba(0,0,0,0.6);
    }
    .poster::before {
      content: '';
      position: absolute;
      top: -20%; right: -20%;
      width: 70%; height: 60%;
      background: radial-gradient(ellipse, rgba(6,182,212,0.1) 0%, transparent 70%);
      pointer-events: none;
    }
    .poster::after {
      content: '';
      position: absolute;
      bottom: 0; left: 0; right: 0;
      height: 30%;
      background: linear-gradient(to top, rgba(0,0,0,0.35) 0%, transparent 100%);
      pointer-events: none;
    }
    .deco-line { position: absolute; left: 50px; right: 50px; height: 2px; background: rgba(6,182,212,0.3); z-index: 1; }
    .deco-line-1 { top: 130px; }
    .deco-line-2 { top: 137px; height: 1px; background: rgba(6,182,212,0.12); }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 25px; position: relative; z-index: 2; }
    .brand-name { font-family: 'Playfair Display', serif; font-size: 46px; font-weight: 400; color: #fff; letter-spacing: 8px; text-transform: uppercase; }
    .brand-url { font-size: 11px; color: #06b6d4; letter-spacing: 5px; margin-top: 6px; text-transform: uppercase; font-weight: 500; }
    .header-tagline { text-align: right; padding-top: 4px; }
    .header-tagline span { display: block; font-size: 16px; color: rgba(255,255,255,0.8); letter-spacing: 3px; font-weight: 300; text-transform: uppercase; line-height: 1.4; }
    .header-tagline span:last-child { color: #f59e0b; font-weight: 600; }
    .tournament-hero { position: relative; z-index: 2; margin-bottom: 25px; }
    .sport-badge { display: inline-block; background: rgba(6,182,212,0.15); border: 1px solid rgba(6,182,212,0.4); color: #06b6d4; font-size: 11px; font-weight: 700; letter-spacing: 3px; text-transform: uppercase; padding: 6px 16px; margin-bottom: 12px; }
    .tournament-name { font-family: 'Playfair Display', serif; font-size: 42px; font-weight: 700; color: #fff; line-height: 1.15; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px; }
    .tournament-format { font-size: 16px; color: #f59e0b; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 20px; position: relative; z-index: 2; }
    .info-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(6,182,212,0.18); padding: 20px 20px; position: relative; }
    .info-card::before { content: ''; position: absolute; left: 0; top: 20px; bottom: 20px; width: 3px; background: linear-gradient(to bottom, #06b6d4, #0a4a7a); }
    .info-card.highlight { border-color: rgba(245,158,11,0.3); background: rgba(245,158,11,0.04); }
    .info-card.highlight::before { background: linear-gradient(to bottom, #f59e0b, #d97706); }
    .info-label { font-size: 10px; color: #06b6d4; letter-spacing: 3px; text-transform: uppercase; font-weight: 700; margin-bottom: 10px; }
    .info-card.highlight .info-label { color: #f59e0b; }
    .info-value { font-family: 'Playfair Display', serif; font-size: 24px; color: #fff; line-height: 1.2; text-transform: uppercase; letter-spacing: 1px; }
    .info-sub { font-size: 13px; color: rgba(255,255,255,0.7); margin-top: 6px; line-height: 1.4; }
    .info-sub strong { color: #f59e0b; font-weight: 600; }
    .details-row { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; margin-bottom: 20px; position: relative; z-index: 2; }
    .detail-item { text-align: center; padding: 16px 12px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); }
    .detail-icon { font-size: 22px; margin-bottom: 8px; }
    .detail-label { font-size: 9px; color: #06b6d4; letter-spacing: 3px; text-transform: uppercase; font-weight: 700; margin-bottom: 4px; }
    .detail-value { font-size: 15px; color: #fff; font-weight: 600; line-height: 1.3; }
    .detail-value.small { font-size: 12px; color: rgba(255,255,255,0.8); }
    .bottom-grid { display: grid; grid-template-columns: 1.3fr 1fr; gap: 16px; margin-bottom: 20px; position: relative; z-index: 2; }
    .rules-card { background: rgba(255,255,255,0.03); border: 1px solid rgba(6,182,212,0.15); padding: 20px 20px; }
    .rules-card .info-label { margin-bottom: 12px; }
    .rules-text { font-size: 13px; color: rgba(255,255,255,0.75); line-height: 1.6; }
    .rules-list { list-style: none; margin-top: 10px; }
    .rules-list li { font-size: 12px; color: rgba(255,255,255,0.7); padding: 5px 0 5px 16px; position: relative; line-height: 1.4; }
    .rules-list li::before { content: ''; position: absolute; left: 0; top: 50%; transform: translateY(-50%); width: 4px; height: 4px; background: #f59e0b; border-radius: 50%; }
    .contact-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(6,182,212,0.18); padding: 20px 20px; position: relative; }
    .contact-card::before { content: ''; position: absolute; left: 0; top: 20px; bottom: 20px; width: 3px; background: linear-gradient(to bottom, #06b6d4, #0a4a7a); }
    .contact-item { margin-bottom: 12px; }
    .contact-item:last-child { margin-bottom: 0; }
    .contact-item-label { font-size: 9px; color: #06b6d4; letter-spacing: 3px; text-transform: uppercase; font-weight: 700; margin-bottom: 3px; }
    .contact-item-value { font-size: 14px; color: #fff; font-weight: 600; }
    .contact-item-value.phone { font-size: 16px; color: #f59e0b; font-weight: 700; letter-spacing: 1px; }
    .cta-section { display: flex; justify-content: space-between; align-items: center; background: rgba(255,255,255,0.03); border: 1px solid rgba(6,182,212,0.2); padding: 24px 35px; position: relative; z-index: 2; }
    .cta-left { display: flex; flex-direction: column; gap: 8px; }
    .cta-status { display: inline-block; background: ${statusColor.bg}; border: 1px solid ${statusColor.border}; color: ${statusColor.text}; font-size: 11px; font-weight: 700; letter-spacing: 3px; text-transform: uppercase; padding: 5px 14px; width: fit-content; }
    .cta-title { font-family: 'Playfair Display', serif; font-size: 34px; color: #fff; line-height: 1.15; text-transform: uppercase; letter-spacing: 2px; }
    .cta-subtitle { font-size: 13px; color: rgba(255,255,255,0.7); letter-spacing: 2px; }
    .cta-subtitle strong { color: #f59e0b; font-weight: 700; }
    .qr-section { display: flex; flex-direction: column; align-items: center; gap: 6px; }
    #qrcode { background: white; padding: 10px; border-radius: 6px; box-shadow: 0 4px 20px rgba(0,0,0,0.3); }
    .qr-label { font-size: 11px; color: #06b6d4; letter-spacing: 3px; text-transform: uppercase; font-weight: 700; }
    .qr-sublabel { font-size: 10px; color: rgba(255,255,255,0.5); letter-spacing: 2px; }
    
    @media print {
      body {
        background: white !important;
        padding: 0 !important;
        margin: 0 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      
      .poster {
        box-shadow: none !important;
        width: 794px !important;
        height: 1200px !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
        background: linear-gradient(160deg, #0a1628 0%, #0d2b4e 40%, #0a3a6a 70%, #0a4a7a 100%) !important;
      }
      
      .poster,
      .info-card,
      .detail-item,
      .rules-card,
      .contact-card,
      .cta-section,
      .sport-badge,
      .cta-status,
      #qrcode,
      .info-card::before,
      .contact-card::before {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      
      .info-card {
        background: rgba(255,255,255,0.04) !important;
      }
      
      .info-card.highlight {
        background: rgba(245,158,11,0.04) !important;
      }
      
      .detail-item {
        background: rgba(255,255,255,0.03) !important;
      }
      
      .rules-card {
        background: rgba(255,255,255,0.03) !important;
      }
      
      .contact-card {
        background: rgba(255,255,255,0.04) !important;
      }
      
      .cta-section {
        background: rgba(255,255,255,0.03) !important;
      }
      
      .sport-badge {
        background: rgba(6,182,212,0.15) !important;
      }
      
      .cta-status {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
    }
  </style>
</head>
<body>
  <div class="poster">
    <div class="deco-line deco-line-1"></div>
    <div class="deco-line deco-line-2"></div>

    <div class="header">
      <div>
        <div class="brand-name">SPORTIZAN</div>
        <div class="brand-url">WWW.SPORTIZAN.COM</div>
      </div>
      <div class="header-tagline">
        <span style="color: #FFD700;">
          ⭐⭐⭐⭐⭐
        </span>
        <span style="color: #FFD700;">
          ⭐⭐⭐⭐⭐
        </span>
        <span style="color: #FFD700;">
          ⭐⭐⭐⭐⭐
        </span>
      </div>
    </div>

    <div class="tournament-hero">
      <div class="sport-badge">${sportEmoji} ${escapeHtml(t_.sportName.toUpperCase())}</div>
      <div class="tournament-name">${escapeHtml(t_.name)}</div>
      <div class="tournament-format">${escapeHtml(formatLabel)}</div>
    </div>

    <div class="info-grid">
      ${t_.prizePool ? `
      <div class="info-card">
        <div class="info-label">${safeT(t, 'tournament.poster.prizePool')}</div>
        <div class="info-value">${escapeHtml(t_.prizePool)}</div>
        <div class="info-sub">${safeT(t, 'tournament.poster.prizePoolSub')}</div>
      </div>` : `
      <div class="info-card">
        <div class="info-label">${safeT(t, 'tournament.poster.location')}</div>
        <div class="info-value" style="font-size:18px">${escapeHtml(locationDisplay)}</div>
        <div class="info-sub">${t_.isOnline ? safeT(t, 'tournament.poster.onlineSub') : safeT(t, 'tournament.poster.physicalSub')}</div>
      </div>`}

      <div class="info-card highlight">
        <div class="info-label">${safeT(t, 'tournament.poster.entryFee')}</div>
        <div class="info-value">${escapeHtml(entryFeeDisplay)}</div>
        <div class="info-sub">${escapeHtml(entryFeeLabel)}</div>
      </div>

      ${t_.prizePool ? `
      <div class="info-card">
        <div class="info-label">${safeT(t, 'tournament.poster.location')}</div>
        <div class="info-value" style="font-size:18px">${escapeHtml(locationDisplay)}</div>
        <div class="info-sub">${t_.isOnline ? safeT(t, 'tournament.poster.onlineSub') : safeT(t, 'tournament.poster.physicalSub')}</div>
      </div>` : ''}

      <div class="info-card">
        <div class="info-label">${safeT(t, 'tournament.poster.dates')}</div>
        <div class="info-value" style="font-size:20px">${escapeHtml(dateRange)}</div>
        <div class="info-sub">${safeT(t, 'tournament.poster.regCloses')} ${escapeHtml(formatDateLong(t_.registrationClosesAt))}</div>
      </div>
    </div>

    <div class="details-row">
      <div class="detail-item">
        <div class="detail-icon">👥</div>
        <div class="detail-label">${safeT(t, 'tournament.poster.teams')}</div>
        <div class="detail-value">${t_.minTeams} – ${t_.maxTeams}</div>
        <div class="detail-value small">${safeT(t, 'tournament.poster.teamsSub', { min: t_.minTeams, max: t_.maxTeams })}</div>
      </div>
      <div class="detail-item">
        <div class="detail-icon">⚖️</div>
        <div class="detail-label">${safeT(t, 'tournament.poster.referees')}</div>
        <div class="detail-value">${escapeHtml(refereesDisplay)}</div>
        ${refereesSubtitle ? `<div class="detail-value small">${escapeHtml(refereesSubtitle)}</div>` : ''}
      </div>
      <div class="detail-item">
        <div class="detail-icon">📝</div>
        <div class="detail-label">${safeT(t, 'tournament.poster.registration')}</div>
        <div class="detail-value">${escapeHtml(registrationModeLabel)}</div>
        <div class="detail-value small">${escapeHtml(registrationModeSubtitle)}</div>
      </div>
    </div>

    <div class="bottom-grid">
      <div class="rules-card">
        <div class="info-label">${safeT(t, 'tournament.poster.rulesFormat')}</div>
        <div class="rules-text">${escapeHtml(rosterFreezeLabel)}${t_.hasThirdPlaceMatch ? '. ' + safeT(t, 'tournament.poster.thirdPlaceIncluded') : ''}</div>
        <ul class="rules-list">
          ${rulesLines.map(l => `<li>${escapeHtml(l)}</li>`).join('\n          ')}
        </ul>
      </div>
      <div class="contact-card">
        <div class="info-label">${safeT(t, 'tournament.poster.contactOrganizer')}</div>
        ${t_.organizerPhone ? `
        <div class="contact-item">
          <div class="contact-item-label">${safeT(t, 'tournament.poster.phone')}</div>
          <div class="contact-item-value phone">${escapeHtml(t_.organizerPhone)}</div>
        </div>` : ''}
        <div class="contact-item">
          <div class="contact-item-label">${safeT(t, 'tournament.poster.organizer')}</div>
          <div class="contact-item-value">${escapeHtml(t_.organizerName)}</div>
        </div>
        <div class="contact-item">
          <div class="contact-item-label">${safeT(t, 'tournament.poster.status')}</div>
          <div class="contact-item-value" style="color: ${statusColor.text};">${escapeHtml(statusLabel)}</div>
        </div>
        <div class="contact-item">
          <div class="contact-item-label">${safeT(t, 'tournament.poster.spotsLeft')}</div>
          <div class="contact-item-value">${t_.registeredTeamsCount} / ${t_.maxTeams} ${safeT(t, 'tournament.poster.teamsRegistered')}</div>
        </div>
      </div>
    </div>

    <div class="cta-section">
      <div class="cta-left">
        <div class="cta-status">${escapeHtml(statusLabel.toUpperCase())}</div>
        <div class="cta-title">${escapeHtml(ctaTitle)}</div>
        <div class="cta-subtitle">${escapeHtml(ctaSubtitle)}</div>
      </div>
      <div class="qr-section">
        <div id="qrcode"></div>
        <div class="qr-label">${safeT(t, 'tournament.poster.scanToView')}</div>
        <div class="qr-sublabel">sportizan.com/tournaments</div>
      </div>
    </div>
  </div>

  <script>
    new QRCode(document.getElementById("qrcode"), {
      text: "${tournamentUrl}",
      width: 140,
      height: 140,
      colorDark: "#0a4a7a",
      colorLight: "#ffffff",
      correctLevel: QRCode.CorrectLevel.H
    });

    // Auto-open print dialog after fonts + QR load
    window.addEventListener('load', function() {
      setTimeout(function() { window.print(); }, 800);
    });
  </script>
</body>
</html>`;
}

export function useTournamentPoster() {
  const { t } = useTranslation();

  const generatePoster = useCallback((tournament: TournamentDto) => {
    const html = buildPosterHtml(tournament, t);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const win = window.open(url, '_blank', 'width=900,height=1280,menubar=yes,toolbar=yes');
    
    if (!win) {
      // Ako je popup blokiran, direktno preuzmi
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tournament.name.replace(/[^a-z0-9]/gi, '_')}_poster.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      // Ako je otvoren, sačekaj da se učita pa pokreni print
      win.onload = function() {
        setTimeout(() => {
          win.print();
        }, 1000);
      };
    }

    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }, [t]);

  // Dodatna funkcija za direktan download bez otvaranja prozora
  const downloadPoster = useCallback((tournament: TournamentDto) => {
    const html = buildPosterHtml(tournament, t);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `${tournament.name.replace(/[^a-z0-9]/gi, '_')}_poster.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [t]);

  return { generatePoster, downloadPoster };
}